import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import collectionsService from '../services/collectionsService';
import {Collection} from '../types/Collection';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate} from "react-router-dom";
import {handleError} from "../../../common/utils/errorHandling";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {getValueWithFallback} from '../../../common/types/StringTranslations';
import {useToast} from "../../../common/contexts/ToastContext";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";

const CollectionsList: React.FC = () => {
  const {t, i18n} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [collections, setCollections] = useState<Collection[]>([]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    title: {value: null, matchMode: FilterMatchMode.CONTAINS}
  });

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async (): Promise<void> => {
    const collectionsData = await collectionsService.getAllCollections();
    setCollections(collectionsData);
  };

  const handleDelete = async (collection: Collection): Promise<void> => {
    const collectionTitle = getValueWithFallback(collection.title, i18n.language);
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: collectionTitle}),
      accept: async () => {
        await handleError(
          async () => {
            await collectionsService.deleteCollection(collection.id);
            await fetchCollections();
          },
          t('admin.collections.errors.deleting'),
          undefined,
          showError
        );
      }
    });
  };

  const handleEdit = (collection: Collection) => {
    navigate(`${collection.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  const personalBodyTemplate = (collection: Collection) => {
    return collection.personal ? (
      <span
        className="px-3 py-1 bg-gray-700 text-white text-xs rounded-sm">{t('admin.collections.status.personal')}</span>
    ) : (
      <span
        className="px-3 py-1 bg-gray-400 text-white text-xs rounded-sm">{t('admin.collections.status.shared')}</span>
    );
  };

  const publishedBodyTemplate = (collection: Collection) => {
    return collection.published ? (
      <span
        className="px-3 py-1 bg-gray-800 text-white text-xs rounded-sm">{t('admin.collections.status.published')}</span>
    ) : (
      <span
        className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded-sm">{t('admin.collections.status.unpublished')}</span>
    );
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.collections.title.list')}
        subtitle={t('admin.collections.subtitle.list')}
      />

      <AdminButton
        onClick={handleCreateNew}
        variant="black"
        label={t('admin.collections.actions.createNew')}
      />

      <DataTable
        value={collections}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        columnResizeMode="fit"
        resizableColumns
        showGridlines
      >
        <Column
          sortField="title"
          header={t('admin.collections.columns.title')}
          sortable
          filter
          filterField="title"
          filterPlaceholder={t('admin.collections.filters.searchByTitle')}
          showFilterMenu={false}
          showClearButton={false}
          body={(collection: Collection) => (
            <strong>{getValueWithFallback(collection.title, i18n.language)}</strong>
          )}/>
        <Column
          sortField="subtitle"
          header={t('admin.collections.columns.subtitle')}
          sortable
          body={(collection: Collection) => (
            <span>{getValueWithFallback(collection.subtitle, i18n.language) || '-'}</span>
          )}/>
        <Column
          sortField="personal"
          header={t('admin.common.columns.type')}
          sortable
          body={personalBodyTemplate}
        />
        <Column
          sortField="published"
          header={t('admin.common.columns.status')}
          sortable
          body={publishedBodyTemplate}
        />
        <Column
          header={t('admin.common.columns.actions')}
          body={(collection: Collection) => (
            <div>
              <Button
                onClick={() => handleEdit(collection)}
                icon="fa fa-edit"
                severity="info"
                size="small"
                outlined
                className="mr-2"
                tooltip={t('admin.common.actions.edit')}
                tooltipOptions={{position: 'top'}}
              />
              <Button
                onClick={() => handleDelete(collection)}
                icon="fa fa-trash"
                severity="danger"
                size="small"
                outlined
                tooltip={t('admin.common.actions.delete')}
                tooltipOptions={{position: 'top'}}
              />
            </div>
          )}
        />
      </DataTable>
    </>
  );
};

export default CollectionsList;


